import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'

import { Caption, H6, Subtitle1 } from '@src/elements/typography/typography'
import { ProgressBar } from '@src/elements/progress-bar/progress-bar'
import { ProgressBarCircle } from '@src/elements/progress-bar-circle/progress-bar-circle'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import { Bullet } from '@src/elements/bullet/bullet'
import { availableEquipment, NO_EQUIPMENT } from '@src/util/constants'

import './composition.sass'
import { ExerciseTagFilter } from '@src/state/store'
import { isMobileBrowser } from '@src/util/functions'
import AIChat from '@src/components/ai-chat-widget/ai-chat-widget'

@Component({
  computed: {
    exerciseFilterTags: {
      get: function () {
        return this.$store.state.exerciseFilters.tags
      }
    },
    exerciseTags () {
      return this.$store.state.exerciseTags
    }
  }
})
export default class Composition extends Vue {

  @Prop({ required: false, default: {} })
  progress: { [key: string]: number }

  @Prop({ required: true })
  title: string

  @Prop({ required: false })
  filterCallback: (exerciseFilterTags: ExerciseTagFilter) => void

  @Prop({ required: false, default: 215 })
  bodyCircleDiameter: number

  @Prop({ required: false, default: 86 })
  muscleCircleDiameter: number

  @Prop({ required: false, default: 86 })
  movementCircleDiameter: number

  @Prop({ required: false, default: true })
  isFilterEnabled: boolean

  @Prop({ required: false, default: false })
  showLegend: boolean

  @Prop({ required: false, default: false })
  straigthMobileProgressBars: boolean

  @Prop({ required: false, default: false })
  showAssistant: boolean

  // Typings for computed properties
  private exerciseTags: any

  // @ts-ignore - Used in watcher, will be used when AI Chat is re-enabled
  private availableTags: any = null
  // @ts-ignore - Used in watcher, will be used when AI Chat is re-enabled
  private aiTags: any = null
  private exerciseFilterTags: ExerciseTagFilter

  // Collapsible state for each category
  private executionExpanded: boolean = true
  private categoriesExpanded: boolean = true
  private bodyAreaExpanded: boolean = true
  private focusExpanded: boolean = true
  private muscleGroupsExpanded: boolean = true
  private equipmentExpanded: boolean = true
  private movementPatternExpanded: boolean = true
  private rangeOfMotionExpanded: boolean = true
  private exerciseTypeExpanded: boolean = true
  private jointsExpanded: boolean = true
  private clinicalPicturesExpanded: boolean = true

  constructor () {
    super()
  }

  toggleExecution () {
    this.executionExpanded = !this.executionExpanded
    this.categoriesExpanded = !this.categoriesExpanded
  }

  toggleCategories () {
    this.categoriesExpanded = !this.categoriesExpanded
    this.executionExpanded = !this.executionExpanded
  }

  toggleBodyArea () {
    this.bodyAreaExpanded = !this.bodyAreaExpanded
  }

  toggleFocus () {
    this.focusExpanded = !this.focusExpanded
  }

  toggleMuscleGroups () {
    this.muscleGroupsExpanded = !this.muscleGroupsExpanded
  }

  toggleEquipment () {
    this.equipmentExpanded = !this.equipmentExpanded
  }

  toggleMovementPattern () {
    this.movementPatternExpanded = !this.movementPatternExpanded
  }

  toggleRangeOfMotion () {
    this.rangeOfMotionExpanded = !this.rangeOfMotionExpanded
    this.exerciseTypeExpanded = !this.exerciseTypeExpanded
  }

  toggleExerciseType () {
    this.exerciseTypeExpanded = !this.exerciseTypeExpanded
    this.rangeOfMotionExpanded = !this.rangeOfMotionExpanded
  }

  toggleJoints () {
    this.jointsExpanded = !this.jointsExpanded
  }

  toggleClinicalPictures () {
    this.clinicalPicturesExpanded = !this.clinicalPicturesExpanded
  }

  @Watch('exerciseTags', { immediate: true, deep: true })
    onExerciseTagsUpdated () {
    if (this.exerciseTags && typeof this.exerciseTags === 'object' && Object.keys(this.exerciseTags).length > 0) {
      this.availableTags = Object.keys(this.exerciseTags).reduce((acc, key) => {
        if (key !== 'ExerciseTags::HealthLimitation') {
          acc[key] = this.exerciseTags[key].map(tag => tag.toLowerCase())
        }
        return acc
      }, {})
      this.aiTags = Object.keys(this.exerciseTags).reduce((acc, key) => {
        if (key !== 'ExerciseTags::ContentType') {
          acc[key] = this.exerciseTags[key].map(tag => tag.toLowerCase())
        }
        return acc
      }, {})
    }
  }
  isFilterSelected (tag, type) {
    return (this.exerciseFilterTags[type] || new Set([])).has(tag)
  }

  filterByTag (tag: string, type: string) {
    console.log(`Added filter tag ${type} : ${tag}`)
    this.exerciseFilterTags[type] = (this.exerciseFilterTags[type] || new Set([])).add(tag)
    this.filterCallback(this.exerciseFilterTags)
  }

  removeFilter (tag: string, type: string) {
    console.log(`Removed filter tag ${type} : ${tag}`)

    if (this.isFilterSelected(tag, type)) {
      this.exerciseFilterTags[type].delete(tag)

      this.filterCallback(this.exerciseFilterTags)
    }
  }

  render (h: CreateElement) {
    if (!this.exerciseTags) {
      return
    }

    const plusTooltip = this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.SUGGEST_TAG')
    const minusTooltip = this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.REMOVE_TAG')
    let muscleGroups = this.exerciseTags['ExerciseTags::MuscleGroup'] ? this.exerciseTags['ExerciseTags::MuscleGroup'].map(muscle => {
      const isFilterSelected = this.isFilterSelected(muscle, 'muscle')
      return <div class={'tag-progress ' + (isFilterSelected ? 'tag-progress-selected' : '')}>
        {isFilterSelected ?
          <Tooltip content={minusTooltip}>
            <span><span class='filter-btn minus' onClick={() => this.removeFilter(muscle, 'muscle')} /></span>
          </Tooltip> :
          <Tooltip content={plusTooltip}>
            <span><span class='filter-btn plus' onClick={() => this.filterByTag(muscle, 'muscle')} /></span>
          </Tooltip>}
        <ProgressBarCircle ref={`tag-progress-${muscle}`} progress={ this.progress && typeof this.progress[muscle] === typeof(1) ? this.progress[muscle] : 0 } image={'/assets/icon/muscle/' + muscle.toLowerCase() + '.svg'}
                           footer={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + muscle.toUpperCase())} diameter={this.muscleCircleDiameter} />
      </div>
    }) : []
    let execution = this.exerciseTags['ExerciseTags::Execution'] ? this.exerciseTags['ExerciseTags::Execution'].map(exec => {
      const isFilterSelected = this.isFilterSelected(exec, 'execution')
      return <div class={'tag-progress ' + (isFilterSelected ? 'tag-progress-selected' : '')}>
        {isFilterSelected ?
          <Tooltip content={minusTooltip}>
            <span><span class='filter-btn minus' onClick={() => this.removeFilter(exec, 'execution')} /></span>
          </Tooltip> :
          <Tooltip content={plusTooltip}>
            <span><span class='filter-btn plus' onClick={() => this.filterByTag(exec, 'execution')} /></span>
          </Tooltip>}
        <ProgressBarCircle ref={`tag-progress-${exec}`} progress={this.progress && typeof this.progress[exec] === typeof(1) ? this.progress[exec] : 0} image={'/assets/icon/execution/' + exec.toLowerCase() + '.svg'}
                           footer={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + exec.toUpperCase())} diameter={this.movementCircleDiameter} />
      </div>
    }) : []
    let categories = this.exerciseTags['ExerciseTags::Category'] ? this.exerciseTags['ExerciseTags::Category'].map(category => {
      const isFilterSelected = this.isFilterSelected(category, 'category')
      return <div class={'tag-progress ' + (isFilterSelected ? 'tag-progress-selected' : '')}>
        {isFilterSelected ?
          <Tooltip content={minusTooltip}>
            <span><span class='filter-btn minus' onClick={() => this.removeFilter(category, 'category')} /></span>
          </Tooltip> :
          <Tooltip content={plusTooltip}>
            <span><span class='filter-btn plus' onClick={() => this.filterByTag(category, 'category')} /></span>
          </Tooltip>}
        <ProgressBarCircle ref={`tag-progress-${category}`} progress={ this.progress && typeof this.progress[category] === typeof(1) ? this.progress[category] : 0 } image={'/assets/icon/category/' + category.toLowerCase() + '.svg'}
                           footer={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + category.toUpperCase())} diameter={this.muscleCircleDiameter} />
      </div>
    }) : []
    let movementPatterns = this.exerciseTags['ExerciseTags::MovementPattern'] ? this.exerciseTags['ExerciseTags::MovementPattern'].map(movement => {
      const isFilterSelected = this.isFilterSelected(movement, 'movement')
      return <Caption class={'focus-item ' + (isFilterSelected ? 'tag-progress-selected' : '')}>
        {this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + movement.toUpperCase())}
        {isFilterSelected ?
          <Tooltip content={minusTooltip}>
            <span><span class='filter-btn minus' onClick={() => this.removeFilter(movement, 'movement')} /></span>
          </Tooltip> :
          <Tooltip content={plusTooltip}>
            <span><span class='filter-btn plus' onClick={() => this.filterByTag(movement, 'movement')} /></span>
          </Tooltip>}
        <ProgressBar ref={`tag-progress-${movement}`} progress={ this.progress && typeof this.progress[movement] === typeof(1) ? this.progress[movement] : 0 } />
      </Caption>
    }) : []
    let joints = this.exerciseTags['ExerciseTags::Joints'] ? this.exerciseTags['ExerciseTags::Joints'].map(joint => {
      const isFilterSelected = this.isFilterSelected(joint, 'joints')
      return <div class={'tag-progress ' + (isFilterSelected ? 'tag-progress-selected' : '')}>
        {isFilterSelected ?
          <Tooltip content={minusTooltip}>
            <span><span class='filter-btn minus' onClick={() => this.removeFilter(joint, 'joints')} /></span>
          </Tooltip> :
          <Tooltip content={plusTooltip}>
            <span><span class='filter-btn plus' onClick={() => this.filterByTag(joint, 'joints')} /></span>
          </Tooltip>}
        <ProgressBarCircle ref={`tag-progress-${joint}`} progress={this.progress && typeof this.progress[joint] === typeof(1) ? this.progress[joint] : 0} image={'/assets/icon/joints/' + joint.toLowerCase() + '.png'}
                           footer={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + joint.toUpperCase())} diameter={this.movementCircleDiameter} />
      </div>
    }) : []

    let rangeOfMotion = this.exerciseTags['ExerciseTags::RangeOfMotion'] ? this.exerciseTags['ExerciseTags::RangeOfMotion'].map(rom => {
      const isFilterSelected = this.isFilterSelected(rom, 'rangeOfMotion')
      return <Caption class={'focus-item ' + (isFilterSelected ? 'tag-progress-selected' : '')}>
        {this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + rom.toUpperCase())}
        {isFilterSelected ?
          <Tooltip content={minusTooltip}>
            <span><span class='filter-btn minus' onClick={() => this.removeFilter(rom, 'rangeOfMotion')} /></span>
          </Tooltip> :
          <Tooltip content={plusTooltip}>
            <span><span class='filter-btn plus' onClick={() => this.filterByTag(rom, 'rangeOfMotion')} /></span>
          </Tooltip>}
        <ProgressBar ref={`tag-progress-${rom}`} progress={ this.progress && typeof this.progress[rom] === typeof(1) ? this.progress[rom] : 0 } />
      </Caption>
    }) : []

    let exerciseType = this.exerciseTags['ExerciseTags::Exercise'] ? this.exerciseTags['ExerciseTags::Exercise'].map(exType => {
      const isFilterSelected = this.isFilterSelected(exType, 'exercise')
      return <Caption class={'focus-item ' + (isFilterSelected ? 'tag-progress-selected' : '')}>
        {this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + exType.toUpperCase())}
        {isFilterSelected ?
          <Tooltip content={minusTooltip}>
            <span><span class='filter-btn minus' onClick={() => this.removeFilter(exType, 'exercise')} /></span>
          </Tooltip> :
          <Tooltip content={plusTooltip}>
            <span><span class='filter-btn plus' onClick={() => this.filterByTag(exType, 'exercise')} /></span>
          </Tooltip>}
        <ProgressBar ref={`tag-progress-${exType}`} progress={ this.progress && typeof this.progress[exType] === typeof(1) ? this.progress[exType] : 0 } />
      </Caption>
    }) : []

    let clinicalPictures = this.exerciseTags['ExerciseTags::ClinicalPictures'] ? this.exerciseTags['ExerciseTags::ClinicalPictures'].map(clinical => {
      const isFilterSelected = this.isFilterSelected(clinical, 'clinicalPictures')
      return <Caption class={'focus-item ' + (isFilterSelected ? 'tag-progress-selected' : '')}>
        {this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + clinical.toUpperCase())}
        {isFilterSelected ?
          <Tooltip content={minusTooltip}>
            <span><span class='filter-btn minus' onClick={() => this.removeFilter(clinical, 'clinicalPictures')} /></span>
          </Tooltip> :
          <Tooltip content={plusTooltip}>
            <span><span class='filter-btn plus' onClick={() => this.filterByTag(clinical, 'clinicalPictures')} /></span>
          </Tooltip>}
        <ProgressBar ref={`tag-progress-${clinical}`} progress={ this.progress && typeof this.progress[clinical] === typeof(1) ? this.progress[clinical] : 0 } />
      </Caption>
    }) : []

    let focus = this.exerciseTags['ExerciseTags::Focus'] ? this.exerciseTags['ExerciseTags::Focus'].map(focusItem => {
      const isFilterSelected = this.isFilterSelected(focusItem, 'focus')
      return <Caption class={'focus-item ' + (isFilterSelected ? 'tag-progress-selected' : '')}>
        {this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + focusItem.toUpperCase())}
        {isFilterSelected ?
          <Tooltip content={minusTooltip}>
            <span><span class='filter-btn minus' onClick={() => this.removeFilter(focusItem, 'focus')} /></span>
          </Tooltip> :
          <Tooltip content={plusTooltip}>
            <span><span class='filter-btn plus' onClick={() => this.filterByTag(focusItem, 'focus')} /></span>
          </Tooltip>}
        <ProgressBar ref={`tag-progress-${focusItem}`} progress={ this.progress && typeof this.progress[focusItem] === typeof(1) ? this.progress[focusItem] : 0 } />
      </Caption>
    }) : []

    let bodyAreaText = this.exerciseTags['ExerciseTags::BodyArea'] ? this.exerciseTags['ExerciseTags::BodyArea'].map((body) => {
      const isFilterSelected = this.isFilterSelected(body, 'body')
      return <Caption class={'body-filter-tag ' + (isFilterSelected ? 'tag-progress-selected' : '')}>
        {isFilterSelected ?
          <Tooltip content={minusTooltip}>
            <span><span class='filter-btn minus' onClick={() => this.removeFilter(body, 'body')} /></span>
          </Tooltip> :
          <Tooltip content={plusTooltip}>
            <span><span class='filter-btn plus' onClick={() => this.filterByTag(body, 'body')} /></span>
          </Tooltip>}
        {this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + body.toUpperCase())}
        <br />
      </Caption>
    }) : []

    let equipmentList = [NO_EQUIPMENT, ...availableEquipment].map(eq => {
      const isFilterSelected = this.isFilterSelected(eq, 'equipment')
      const imgPath = eq === NO_EQUIPMENT ? '/assets/icon/util/close.svg' : '/assets/icon/equipment/' + eq + '.svg'
      return <div class={'tag-progress ' + (isFilterSelected ? 'tag-progress-selected' : '')}>
        {isFilterSelected ?
          <Tooltip content={minusTooltip}>
            <span><span class='filter-btn minus' onClick={() => this.removeFilter(eq, 'equipment')} /></span>
          </Tooltip> :
          <Tooltip content={plusTooltip}>
            <span><span class='filter-btn plus' onClick={() => this.filterByTag(eq, 'equipment')} /></span>
          </Tooltip>}
        <ProgressBarCircle ref={`tag-progress-${eq}`} progress={ this.progress && this.progress[eq] ? this.progress[eq] : 0 } image={imgPath}
                           footer={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + eq.toUpperCase())} diameter={this.muscleCircleDiameter} />
      </div>
    })

    // standard diameters will be: 180 145 110 75
    let bodyAreaCirclesDiameter = this.bodyCircleDiameter
    let bodyAreas = this.exerciseTags['ExerciseTags::BodyArea'] ? this.exerciseTags['ExerciseTags::BodyArea'].map((body) => {
      bodyAreaCirclesDiameter -= 35
      return isMobileBrowser() && this.straigthMobileProgressBars ? <ProgressBar ref={`tag-progress-${bodyAreas}`} progress={this.progress[body] || 0} />
          : <ProgressBarCircle strokeWidth={6} progress={ this.progress && typeof this.progress[body] === typeof(1) ? this.progress[body] : 0 } diameter={bodyAreaCirclesDiameter} class='combined-circle' croppedProgress={true} />
    }) : []

    // to be used for printing style
    const isSafari = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1

    return <div class={`workout-composition ${isSafari ? 'isSafari' : ''}`}>
      <H6 class='column-header title'>
        {this.$t(this.title)}
        {this.showLegend ?
          <Caption class='workout-composition-legend'>
            <Bullet color='cyan' />{this.$t('HIGH')}
            <Bullet color='yellow' />{this.$t('MODERATE')}
            <Bullet color='red' />{this.$t('LOW')}
            <Bullet color='grey' />{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.NOT_TRAINED')}
          </Caption> : null}
      </H6>

      <div class={'composition' + (!this.isFilterEnabled ? ' filter-disabled' : '')}>
        <div class='top-filters'>
          <div class='execution-filter'>
            <div class='collapsible-header' onClick={this.toggleExecution}>
              <span class={`collapse-icon ${this.executionExpanded ? 'expanded' : 'collapsed'}`}>▼</span>
              <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.EXECUTION_PLACEHOLDER')}</Subtitle1>
            </div>
            <transition name='collapse'>
              {this.executionExpanded && (
                <div class='composition-wrapper'>
                  {execution}
                </div>
              )}
            </transition>
          </div>

          <div class='categories-filter'>
            <div class='collapsible-header' onClick={this.toggleCategories}>
              <span class={`collapse-icon ${this.categoriesExpanded ? 'expanded' : 'collapsed'}`}>▼</span>
              <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.CATEGORY_PLACEHOLDER')}</Subtitle1>
            </div>
            <transition name='collapse'>
              {this.categoriesExpanded && (
                <div class='composition-wrapper'>
                  {categories}
                </div>
              )}
            </transition>
          </div>
        </div>

        <div class='body-area'>
          <div class='collapsible-header' onClick={this.toggleBodyArea}>
            <span class={`collapse-icon ${this.bodyAreaExpanded ? 'expanded' : 'collapsed'}`}>▼</span>
            <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.BODY_PLACEHOLDER')}</Subtitle1>
          </div>
          <transition name='collapse'>
            {this.bodyAreaExpanded && (
              <div id='combined-circle-wrapper-parent'>
                <div class='combined-circle-wrapper'>
                    <div class='body-filters'>
                      {bodyAreaText}
                    </div>
                    <div class='body-areas'>{bodyAreas}</div>
                </div>
              </div>
            )}
          </transition>
        </div>

        {this.isFilterEnabled && <div class='focus'>
          <div class='collapsible-header' onClick={this.toggleFocus}>
            <span class={`collapse-icon ${this.focusExpanded ? 'expanded' : 'collapsed'}`}>▼</span>
            <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.FOCUS_PLACEHOLDER')}</Subtitle1>
          </div>
          <transition name='collapse'>
            {this.focusExpanded && (
              <div class='focus-progress composition-wrapper'>
                {focus}
              </div>
            )}
          </transition>
        </div>}

        <div class='muscle-groups'>
          <div class='collapsible-header' onClick={this.toggleMuscleGroups}>
            <span class={`collapse-icon ${this.muscleGroupsExpanded ? 'expanded' : 'collapsed'}`}>▼</span>
            <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.MUSCLE_PLACEHOLDER')}</Subtitle1>
          </div>
          <transition name='collapse'>
            {this.muscleGroupsExpanded && (
              <div class='composition-wrapper'>
                {muscleGroups}
              </div>
            )}
          </transition>
        </div>

        {this.isFilterEnabled && <div class='equipment'>
          <div class='collapsible-header' onClick={this.toggleEquipment}>
            <span class={`collapse-icon ${this.equipmentExpanded ? 'expanded' : 'collapsed'}`}>▼</span>
            <Subtitle1>{this.$t('EQUIPMENT')}</Subtitle1>
          </div>
          <transition name='collapse'>
            {this.equipmentExpanded && (
              <div class='composition-wrapper'>
                {equipmentList}
              </div>
            )}
          </transition>
        </div>}

        {this.isFilterEnabled && <div class='movement-pattern'>
          <div class='collapsible-header' onClick={this.toggleMovementPattern}>
            <span class={`collapse-icon ${this.movementPatternExpanded ? 'expanded' : 'collapsed'}`}>▼</span>
            <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.MOVEMENT_PLACEHOLDER')}</Subtitle1>
          </div>
          <transition name='collapse'>
            {this.movementPatternExpanded && (
              <div class='focus-progress composition-wrapper'>
                {movementPatterns}
              </div>
            )}
          </transition>
        </div>}

        {this.isFilterEnabled && <div class='additional-filters'>
          <div class='range-of-motion'>
            <div class='collapsible-header' onClick={this.toggleRangeOfMotion}>
              <span class={`collapse-icon ${this.rangeOfMotionExpanded ? 'expanded' : 'collapsed'}`}>▼</span>
              <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.RANGE_OF_MOTION_PLACEHOLDER')}</Subtitle1>
            </div>
            <transition name='collapse'>
              {this.rangeOfMotionExpanded && (
                <div class='focus-progress composition-wrapper'>
                  {rangeOfMotion}
                </div>
              )}
            </transition>
          </div>

          <div class='exercise-type'>
            <div class='collapsible-header' onClick={this.toggleExerciseType}>
              <span class={`collapse-icon ${this.exerciseTypeExpanded ? 'expanded' : 'collapsed'}`}>▼</span>
              <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.EXERCISE_TYPE_PLACEHOLDER')}</Subtitle1>
            </div>
            <transition name='collapse'>
              {this.exerciseTypeExpanded && (
                <div class='focus-progress composition-wrapper'>
                  {exerciseType}
                </div>
              )}
            </transition>
          </div>
        </div>}

        {this.isFilterEnabled && <div class='joints'>
          <div class='collapsible-header' onClick={this.toggleJoints}>
            <span class={`collapse-icon ${this.jointsExpanded ? 'expanded' : 'collapsed'}`}>▼</span>
            <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.JOINTS_PLACEHOLDER')}</Subtitle1>
          </div>
          <transition name='collapse'>
            {this.jointsExpanded && (
              <div class='composition-wrapper'>
                {joints}
              </div>
            )}
          </transition>
        </div>}

        {this.isFilterEnabled && <div class='clinical-pictures'>
          <div class='collapsible-header' onClick={this.toggleClinicalPictures}>
            <span class={`collapse-icon ${this.clinicalPicturesExpanded ? 'expanded' : 'collapsed'}`}>▼</span>
            <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.CLINICAL_PICTURE_PLACEHOLDER')}</Subtitle1>
          </div>
          <transition name='collapse'>
            {this.clinicalPicturesExpanded && (
              <div class='focus-progress composition-wrapper'>
                {clinicalPictures}
              </div>
            )}
          </transition>
        </div>}
      </div>
      {this.showAssistant && this.availableTags && (
          <AIChat
              availableTags={this.aiTags}
              filterFunction={this.filterByTag}
              removeFilterFunction={this.removeFilter}
          />
        )
      }
    </div>
  }
}
